/**
 * useProductAccess Hook
 *
 * Convenience hook for checking product access with upgrade prompt support.
 * This is for UX purposes only - actual security is enforced via RLS policies.
 *
 * @see docs/architecture/subscription-database-schema.md
 */

import { useMemo } from 'react';
import { useSubscription } from '../shared/SubscriptionContext';
import type {
  ProductCode,
  ProductTier,
  ProductAccessResult,
  TierAccessResult,
  PRODUCT_DISPLAY_NAMES,
  TIER_DISPLAY_NAMES,
} from '@xanectech/types';

// Re-export display names for convenience
export { PRODUCT_DISPLAY_NAMES, TIER_DISPLAY_NAMES } from '@xanectech/types';

// ============================================
// Tier Hierarchy
// ============================================

const TIER_ORDER: ProductTier[] = [
  'free',
  'plus',
  'bronze',
  'silver',
  'gold',
  'platinum',
];

function getNextTier(currentTier: ProductTier | null): ProductTier {
  if (!currentTier) return 'plus';
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex === TIER_ORDER.length - 1) {
    return 'platinum';
  }
  const nextTier = TIER_ORDER[currentIndex + 1];
  return nextTier ?? 'platinum';
}

// ============================================
// Hooks
// ============================================

/**
 * Check if user has access to a specific product
 *
 * @param productCode - The product to check access for
 * @returns ProductAccessResult with access status and upgrade info
 *
 * @example
 * ```tsx
 * const { hasAccess, needsUpgrade, recommendedTier } = useProductAccess('estate_vault');
 *
 * if (!hasAccess) {
 *   return <UpgradePrompt product="estate_vault" tier={recommendedTier} />;
 * }
 * ```
 */
export function useProductAccess(
  productCode: ProductCode,
): ProductAccessResult {
  const {
    hasProductAccess,
    getProductTier,
    isProductTrial,
    products,
    isLoading,
  } = useSubscription();

  return useMemo(() => {
    const hasAccess = hasProductAccess(productCode);
    const currentTier = getProductTier(productCode);
    const isTrial = isProductTrial(productCode);

    // Get trial end date
    const product = products.find((p) => p.product_code === productCode);
    const trialEndDate = product?.trial_end
      ? new Date(product.trial_end)
      : null;

    // Calculate trial days remaining
    let trialDaysRemaining: number | null = null;
    if (isTrial && trialEndDate) {
      const now = new Date();
      const diffMs = trialEndDate.getTime() - now.getTime();
      trialDaysRemaining = Math.max(
        0,
        Math.ceil(diffMs / (1000 * 60 * 60 * 24)),
      );
    }

    return {
      hasAccess,
      currentTier,
      isTrial,
      trialEndDate,
      trialDaysRemaining,
      isLoading,
      needsUpgrade: !hasAccess,
      recommendedTier: getNextTier(currentTier),
    };
  }, [
    productCode,
    hasProductAccess,
    getProductTier,
    isProductTrial,
    products,
    isLoading,
  ]);
}

/**
 * Check if user has access at or above a specific tier for a product
 *
 * @param productCode - The product to check
 * @param minTier - The minimum tier required
 * @returns TierAccessResult with tier requirement status
 *
 * @example
 * ```tsx
 * const { meetsTierRequirement, needsUpgrade } = useTierAccess('estate_vault', 'silver');
 *
 * if (!meetsTierRequirement) {
 *   return <UpgradePrompt product="estate_vault" tier="silver" />;
 * }
 * ```
 */
export function useTierAccess(
  productCode: ProductCode,
  minTier: ProductTier,
): TierAccessResult {
  const { hasTierAccess, ...rest } = useSubscription();
  const productAccess = useProductAccess(productCode);

  return useMemo(() => {
    const meetsTierRequirement = hasTierAccess(productCode, minTier);

    return {
      ...productAccess,
      meetsTierRequirement,
      requiredTier: minTier,
      needsUpgrade: !meetsTierRequirement,
      recommendedTier: meetsTierRequirement
        ? productAccess.currentTier || minTier
        : minTier,
    };
  }, [productCode, minTier, hasTierAccess, productAccess]);
}

/**
 * Get all products the user has access to
 *
 * @returns Array of product codes the user can access
 */
export function useAccessibleProducts(): ProductCode[] {
  const { products, isLoading } = useSubscription();

  return useMemo(() => {
    if (isLoading) return ['core']; // Default during loading

    return products
      .filter((p) => p.status === 'active' || p.status === 'trial')
      .map((p) => p.product_code);
  }, [products, isLoading]);
}
